import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BUSINESS } from "@/lib/siteData";
import Logo from "@/components/site/Logo";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-secondary border-t border-border mt-0">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <Logo className="h-11 w-11" />
              <img
                src="/logo-wordmark.png"
                alt={`${BUSINESS.name} — ${BUSINESS.tagline}`}
                className="h-8 md:h-10 w-auto select-none"
                draggable="false"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-sm">
              Honest, transparent Windows PC and macOS repair. Established {BUSINESS.established}.
              Flat-rate pricing, written quotes, 30-day workmanship guarantee.
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-overline text-primary mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 text-primary" />
                <a data-testid="footer-phone" href={BUSINESS.phoneHref} className="hover:underline">{BUSINESS.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 text-primary" />
                <a data-testid="footer-email" href={BUSINESS.emailHref} className="hover:underline break-all">{BUSINESS.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-primary" />
                <span data-testid="footer-address">{BUSINESS.address}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5 text-primary" />
                <span>{BUSINESS.hours}</span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-overline text-primary mb-4">Sitemap</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="/#services" className="hover:underline">Services</a></li>
              <li><a href="/#pricing" className="hover:underline">Pricing</a></li>
              <li><a href="/#process" className="hover:underline">How It Works</a></li>
              <li><Link to="/blog" data-testid="footer-blog-link" className="hover:underline">Blog</Link></li>
              <li><a href="/#faq" className="hover:underline">FAQ</a></li>
              <li><a href="/#contact" className="hover:underline">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-overline text-primary mb-4">Policies</h4>
            <ul className="space-y-2 text-sm">
              <li><Link data-testid="footer-privacy-link" to="/privacy" className="hover:underline">Privacy Policy</Link></li>
              <li><Link data-testid="footer-cookies-link" to="/cookies" className="hover:underline">Cookie Policy</Link></li>
              <li><Link data-testid="footer-terms-link" to="/terms" className="hover:underline">Terms of Service</Link></li>
              <li><Link data-testid="footer-refund-link" to="/refund" className="hover:underline">Refund Policy</Link></li>
              <li>
                <button
                  type="button"
                  data-testid="footer-cookie-settings"
                  onClick={() => window.dispatchEvent(new CustomEvent("rp:open-cookie-preferences"))}
                  className="hover:underline text-left"
                >
                  Cookie settings
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div
          data-testid="independence-disclaimer"
          className="mt-12 pt-8 border-t border-border text-xs text-muted-foreground leading-relaxed"
        >
          <p className="font-semibold text-foreground mb-2">Independent Service Disclaimer</p>
          <p>
            {BUSINESS.name} is an independent third-party computer support and repair service. We are <strong>not affiliated
            with, endorsed by, or sponsored by Microsoft Corporation, Apple Inc.</strong>, or any other brand mentioned on this
            site. All product names, logos and brands are property of their respective owners. Use of these names, logos
            and brands does not imply endorsement. Any reference to Windows&reg;, macOS&reg;, MacBook&reg; or related
            products is for descriptive purposes only.
          </p>
          <p className="mt-3">
            Pricing displayed on this site is in USD and applicable to standard residential repairs only.
            Final pricing depends on the written diagnostic. We will never cold-call you to claim your computer is infected.
          </p>
          <p className="mt-4">
            <span className="font-semibold text-foreground">Registered office:</span> {BUSINESS.registeredAddress}.
            {" "}<span className="font-semibold text-foreground">Operations centre:</span> {BUSINESS.operationsAddress}. No walk-ins.
          </p>
          <p className="mt-3">&copy; {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
